import { getMedias } from "@omni/mappers/product/shared/media.mapper";
import { OrderItem as OrderItemModel } from "@omni/types/api/cart/bff/shared/orderItem.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import * as OrderUtils from "@omni/utils/order.utils";
import { cn } from "@omni/utils/styles";
import { FC } from "react";

import { OrderImage } from "@/components/Account/OrderList/Order/OrderItem/partials/orderImage.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { TooltipType } from "@/components/General/Tooltip/tooltip.types";
import { AdditionalServicesComponent } from "@/components/Order/shared/OrderItem/components/AdditionalServices/additionalServices.component";
import { ArticleConfigurationComponent } from "@/components/Order/shared/OrderItem/components/ArticleConfiguration/articleConfiguration.component";
import { HeaderComponent } from "@/components/Order/shared/OrderItem/components/Header/header.component";
import { PriceComponent } from "@/components/Order/shared/OrderItem/components/Price/price.component";
import { QuantityComponent } from "@/components/Order/shared/OrderItem/components/Quantity/quantity.component";
import { ReferenceComponent } from "@/components/Order/shared/OrderItem/components/Reference/reference.component";
import { WriteReview } from "@/components/Order/shared/OrderItem/components/WriteReview/writeReview.container";
import {
    ConfigurableProductRenditions,
    OrderDetailItemRenditions
} from "@/components/Product/ProductImage/mappedRenditions";
import { useFeatureToggle } from "@/context/featureToggles.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import {
    useBreakpoint,
    withBreakpoints
} from "@/utils/breakpoints/breakpoints.provider";
import { Config } from "@/utils/config.utils";

export type DeliveryPromiseComponentProps = {
    item: OrderItemModel;
    deliveryType: DeliveryType;
};

type Props = {
    item: OrderItemModel;
    additionalServiceTooltip?: TooltipType;
    deliveryType: DeliveryType;
    productTitleLink: boolean;
    reviewsAllowed: boolean;
    DeliveryPromiseComponent: FC<DeliveryPromiseComponentProps>;
};

const _OrderItem = ({
    item,
    additionalServiceTooltip,
    deliveryType,
    productTitleLink,
    reviewsAllowed,
    DeliveryPromiseComponent
}: Props) => {
    const { t } = useGranite();
    const { breakpoints } = useBreakpoint();

    const reviewsEnabled = useFeatureToggle(FeatureToggleKey.FEATURE_REVIEWS);

    const canWriteReview =
        reviewsEnabled &&
        reviewsAllowed &&
        !OrderUtils.isBoxEntry(item) &&
        !item.warrantyDeposit &&
        !item.product?.giftCardArticle;

    return (
        <Row className={"my-2 align-items-center"}>
            <Col
                xs={3}
                sm={2}
                className={"align-self-start"}
            >
                <OrderImage
                    medias={getMedias(
                        item.warrantyDeposit,
                        OrderUtils.getTitle(item, t),
                        item.product?.mainImages,
                        item.articleConfiguration
                    )}
                    title={OrderUtils.getTitle(item, t)}
                    url={OrderUtils.getUrl(item, Config.pages.giftcard)}
                    imageRenditions={
                        item.articleConfiguration
                            ? ConfigurableProductRenditions
                            : OrderDetailItemRenditions
                    }
                />
            </Col>
            <Col
                xs={9}
                md={5}
            >
                <HeaderComponent
                    item={item}
                    link={productTitleLink}
                />
                <ArticleConfigurationComponent item={item} />
                <ReferenceComponent item={item} />

                {!breakpoints.md && (
                    <>
                        <QuantityComponent quantity={item.quantity} />
                        <DeliveryPromiseComponent
                            item={item}
                            deliveryType={deliveryType}
                        />
                    </>
                )}
            </Col>
            {breakpoints.md && (
                <Col md={3}>
                    <QuantityComponent quantity={item.quantity} />
                    <DeliveryPromiseComponent
                        item={item}
                        deliveryType={deliveryType}
                    />
                </Col>
            )}
            <Col
                xs={12}
                md={2}
                className={"text-right pt-1 pb-3 py-md-0"}
            >
                <PriceComponent item={item} />
            </Col>
            <AdditionalServicesComponent
                additionalServices={item.additionalServices ?? []}
                additionalServiceTooltip={additionalServiceTooltip}
            />
            {canWriteReview && (
                <div className={cn("tw:w-full tw:flex tw:justify-end")}>
                    <WriteReview item={item} />
                </div>
            )}
        </Row>
    );
};

export const OrderItem = withBreakpoints(_OrderItem);
